import { getEnv } from '@/composables/useEnv'
import { useHandleError } from '@/composables/useHandleError'
import { useBannerStore } from '@/store/banner'
import { useModalStore } from '@/store/modal'
import { useUserStore } from '@/store/user'
import { RateLimitError } from '@/utils/rateLimit'
import axios, { AxiosInstance } from 'axios'
import { RouteLocationRaw, useRoute, useRouter } from 'vue-router'

type TApiKey = 'express' | 'cloudflare' | string
const axiosInstances: Record<TApiKey, AxiosInstance> = {}

export default function useApi(apiKey: TApiKey): AxiosInstance {
  const userStore = useUserStore()
  const modalStore = useModalStore()
  const bannerStore = useBannerStore()
  const route = useRoute()
  const { logError } = useHandleError()
  const router = useRouter()

  if (axiosInstances[apiKey]) return axiosInstances[apiKey]

  const appVersion = getEnv('VITE_APP_COMMIT_SHA') ?? 'review'

  const axiosInstance = axios.create({
    baseURL: getApiBaseUrl(apiKey),
    headers: {
      common: {
        version: appVersion
      }
    }
  })

  axiosInstance.interceptors.request.use(
    (request) => {
      // hide invalid version modal proactively
      modalStore.invalidVersionModalShowing = false

      // Add current token to all requests
      request.headers.Authorization = `Bearer ${userStore.token}`
      return request
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  axiosInstance.interceptors.response.use(
    (response) => response, // Return response for all 2xx status codes.
    async (error) => {
      let preventException = false
      const originalRequest = error.config
      const useDefaultErrorHandler =
        originalRequest?.useDefaultErrorHandler ?? true

      // TODO: Add error mapping factory to entire section
      // to handle different error types and clean this up
      if (!error?.response) {
        // response may be undefined if there are connectivity issues
        if (error.code === 'ERR_NETWORK') preventException = true
      } else if (error.response.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true // Add a flag to prevent infinite loop
        // Log out user and redirect to login page with current path as redirect query
        userStore.logout()
        router.push({
          name: 'login',
          query: { redirect: router.currentRoute.value.path }
        })
        preventException = true
      } else if (error.response.status === 403) {
        if (
          error.response.data?.meta?.message ===
          'Workshop is not public anymore'
        ) {
          router.push({
            name: 'sketch-closed',
            query: { workshopId: error.response.data.meta.workshopId }
          })
        }
        preventException = true
      } else if (error.response.status === 404 && useDefaultErrorHandler) {
        // redirect to login if 404 (unauthenticated or not found)
        // Callers that need to surface their own 404 UI must opt out via
        // { useDefaultErrorHandler: false } on the request config.
        const redirectTo: RouteLocationRaw = { name: 'login' }
        if (route.query) {
          // if ?from=participant-link, keep the query to show "no access" toast
          redirectTo.query = route.query
        }
        router.push(redirectTo)
        preventException = true
      } else if (
        error.response.status === 400 &&
        error.response.data?.error === 'INVALID_VERSION'
      ) {
        modalStore.invalidVersionModalShowing = true
        preventException = true
      } else if (error.response.status === 429) {
        const isRead = originalRequest.method?.toLowerCase() === 'get'
        if (isRead || !useDefaultErrorHandler) {
          return Promise.reject(new RateLimitError(error.request?.responseURL))
        }
        bannerStore.flashRateLimited()
        preventException = true
      } else if (useDefaultErrorHandler && error.response.status < 500) {
        // send all other errors to appsignal (ie- invalid params)
        const requestUrl = error.request?.responseURL
        let appsignalAction: string | undefined
        if (requestUrl) {
          const baseUrlPattern = /(http\S*\/api)/i
          const uuidPattern =
            /[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}/gi
          appsignalAction = requestUrl
            .replace(uuidPattern, '*')
            .replace(baseUrlPattern, 'api')
        }

        logError(
          error,
          `API error ${error.response.status}`,
          `${originalRequest.method} ${appsignalAction}`
        )
        preventException = true
      }

      if (preventException) return Promise.resolve(error.response)
      // Pass through everything else (Promise.reject triggers exception which is sent to AppSignal)
      return Promise.reject(error)
    }
  )

  axiosInstances[apiKey] = axiosInstance
  return axiosInstance
}

const getApiBaseUrl = (apiKey: TApiKey): string => {
  if (apiKey === 'express') {
    return getExpressBaseUrl() + '/api'
  }

  throw new Error(`Base URL not defined for API key: ${apiKey}`)
}

const getExpressBaseUrl = (): string => {
  let baseUrl = getEnv('VITE_APP_EXPRESS_BASE_URL')
  const reviewAppName = getEnv('HEROKU_APP_NAME')
  if (reviewAppName) {
    baseUrl = `https://${reviewAppName}.herokuapp.com`
  }
  return baseUrl
}

const useCoreApi = () => {
  return useApi('express')
}

export { getExpressBaseUrl, useCoreApi }
